package com.example.mystybag.login.logic

import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.common.resources.SR
import com.example.mystybag.login.model.MerchantRegistrationAddCompanyDetailsUIState
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CheckMerchantBulstatExistsRequestModel
import kotlinx.coroutines.launch
import kotlinx.coroutines.CompletableDeferred

class MerchantRegistrationAddCompanyDetailsViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep1
) : ChildViewModel<MerchantRegistrationAddCompanyDetailsUIState>(context, MerchantRegistrationAddCompanyDetailsUIState()) {

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig
    private val initialBulstat: String = sharedConfig.bulstat

    init {
        updateState {
            it.copy(
                bulstat = sharedConfig.bulstat,
                privacyPolicyAccepted = sharedConfig.privacyPolicyAccepted
            )
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
    }

    fun toggleTermsAccepted() {
        val newValue = !uiState.value.privacyPolicyAccepted
        sharedConfig.privacyPolicyAccepted = newValue
        updateState { it.copy(privacyPolicyAccepted = newValue, privacyPolicyError = false) }
    }


    fun updateBulstat(bulstat: String) {
        // Validate BULSTAT format
        val error = validateBulstat(bulstat)
        updateState { 
            it.copy(
                bulstat = bulstat,
                bulstatError = error
            ) 
        }
        sharedConfig.bulstat = bulstat
    }
    
    /**
     * Validates BULSTAT format
     * BULSTAT can be:
     * - 9 digits (for individuals/sole proprietors)
     * - 13 digits (for legal entities)
     * Must contain only digits
     */
    private fun validateBulstat(bulstat: String): String? {
        if (bulstat.isBlank()) {
            return null // Empty is OK, will be validated on continue
        }
        
        // Remove any spaces or dashes
        val cleanedBulstat = bulstat.replace(" ", "").replace("-", "")
        
        // Check if contains only digits
        if (!cleanedBulstat.all { it.isDigit() }) {
            return sharedString(SR.strings.bulstat_invalid_format)
        }
        
        // Check length (must be 9 or 13 digits)
        if (cleanedBulstat.length != 9 && cleanedBulstat.length != 13) {
            return sharedString(SR.strings.bulstat_invalid_length)
        }
        
        return null // Valid
    }


    
    override fun onBackButtonClicked() {
        // Update shared config with current state before going back
        val state = uiState.value
        sharedConfig.bulstat = state.bulstat
        
        // Update current config in stack
        val updatedConfig = config.copy(sharedConfig = sharedConfig)
        replaceLast(updatedConfig)
        
        super.onBackButtonClicked()
    }

    fun onContinue() {
        val state = uiState.value
        
        // Validate BULSTAT before continuing
        val bulstatError = validateBulstat(state.bulstat)
        if (bulstatError != null) {
            updateState { it.copy(bulstatError = bulstatError) }
            return
        }
        
        // Check if BULSTAT is not blank
        if (state.bulstat.isBlank()) {
            updateState { 
                it.copy(bulstatError = sharedString(SR.strings.bulstat_required)) 
            }
            return
        }

        if (!state.privacyPolicyAccepted) {
            updateState { it.copy(privacyPolicyError = true) }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        // If BULSTAT changed after returning to this screen, clear previously collected data
        // so the next steps are initialized only with data for the new BULSTAT.
        if (state.bulstat != initialBulstat) {
            clearLoadedRegistrationData()
        }
        
        if (state.bulstat.isNotBlank()) {
            val bulstatNormalized = state.bulstat.replace(" ", "").replace("-", "").filter { it.isDigit() }
            scope.launch {
                updateState { it.copy(isLoading = true) }

                commandBlock(showProgress = true) {
                    CommonCommands.checkMerchantBulstatExists(
                        CheckMerchantBulstatExistsRequestModel(bulstat = bulstatNormalized)
                    ).onResponse(
                        success = { response ->
                            launchOnMain {
                                if (response.isExistingMerchant) {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(sharedString(SR.strings.merchant_with_this_bulstat_exists))
                                    return@launchOnMain
                                }

                                // Store canonical BULSTAT (digits only) for APIs and registration payload
                                sharedConfig.bulstat = bulstatNormalized
                                updateState { it.copy(bulstat = bulstatNormalized, bulstatError = null) }

                                // Update current config in stack with updated shared config
                                val updatedConfig = config.copy(sharedConfig = sharedConfig)
                                replaceLast(updatedConfig)

                                // Fetch company data from companybook.bg
                                scope.launch {
                                    try {
                                        val companyData = CommonCommands.getCompanyBookData(bulstatNormalized)

                                        // Try to find matching city and country IDs from our nomenclatures
                                        // Use CompletableDeferred to wait for both API calls
                                        val citiesDeferred = CompletableDeferred<List<com.example.mystybag.sharedmodels.project.responseModels.CityResponseModel>>()
                                        val countriesDeferred = CompletableDeferred<List<com.example.mystybag.sharedmodels.common.responseModels.CountryModel>?>()

                                        // Load cities
                                        commandBlock(showProgress = false) {
                                            CommonCommands.sendGetCities()
                                                .onResponse(
                                                    success = { response ->
                                                        citiesDeferred.complete(response.citiesList)
                                                    },
                                                    error = {
                                                        citiesDeferred.complete(emptyList())
                                                    }
                                                )
                                        }

                                        // Load countries
                                        commandBlock(showProgress = false) {
                                            CommonCommands.sendGetCountries()
                                                .onResponse(
                                                    success = { response ->
                                                        countriesDeferred.complete(response.countriesList)
                                                    },
                                                    error = {
                                                        countriesDeferred.complete(null)
                                                    }
                                                )
                                        }

                                        // Wait for both API calls to complete
                                        val citiesList = citiesDeferred.await()
                                        val countriesList = countriesDeferred.await()

                                        launchOnMain {
                                            // Update shared config with data from CompanyBook API
                                            sharedConfig.legalEntityName = companyData.company.companyName.name

                                            val seat = companyData.company.seat
                                            sharedConfig.registeredCompanyStreet = "${seat.street} ${seat.streetNumber}".trim()

                                            // Try to match city using district from CompanyBook
                                            val districtName = seat.district.uppercase()
                                            val matchedCity = citiesList.find {
                                                it.cityName.uppercase() == districtName ||
                                                    it.cityName.uppercase().contains(districtName) ||
                                                    districtName.contains(it.cityName.uppercase())
                                            }

                                            if (matchedCity != null) {
                                                // If city is found, set both ID and name from our nomenclature
                                                sharedConfig.registeredCompanyCityId = matchedCity.id
                                                sharedConfig.registeredCompanyCityName = matchedCity.cityName
                                            } else {
                                                // If city is not found, leave fields empty for user to fill
                                                sharedConfig.registeredCompanyCityId = null
                                                sharedConfig.registeredCompanyCityName = ""
                                            }

                                            // Try to match country by name - convert both to uppercase for comparison
                                            // This is for the company registration country, not the phone dial code
                                            val countryNameFromAPI = seat.country.uppercase()
                                            val matchedCountry = countriesList?.find { country ->
                                                country.name.uppercase() == countryNameFromAPI ||
                                                    country.name.uppercase().contains(countryNameFromAPI) ||
                                                    countryNameFromAPI.contains(country.name.uppercase())
                                            }

                                            if (matchedCountry != null) {
                                                // If country is found, set both ID and name from our nomenclature
                                                sharedConfig.registeredCompanyCountryId = matchedCountry.id
                                                sharedConfig.registeredCompanyCountryName = matchedCountry.name
                                            } else {
                                                // If country is not found, leave fields empty for user to fill
                                                sharedConfig.registeredCompanyCountryId = null
                                                sharedConfig.registeredCompanyCountryName = ""
                                            }

                                            // Handle phone number - dial code is separate from company registration country
                                            val phone = companyData.company.contacts.phone
                                            if (phone.isNotBlank()) {
                                                var phoneDigits = phone.filter { it.isDigit() }
                                                var dialCode = ""

                                                // Check if phone starts with a dial code from any country
                                                val countries = countriesList // Create local copy to avoid smart cast issues
                                                if (countries != null && countries.isNotEmpty()) {
                                                    // Sort countries by dial code length (longest first) to match longer codes first
                                                    val sortedCountries = countries.sortedByDescending { it.dialCode.filter { char -> char.isDigit() }.length }

                                                    for (country in sortedCountries) {
                                                        val countryDialCode = country.dialCode.filter { it.isDigit() } // Remove + and keep only digits
                                                        if (countryDialCode.isNotEmpty()) {
                                                            // Check if phone starts with dial code (with or without +)
                                                            if (phoneDigits.startsWith(countryDialCode)) {
                                                                dialCode = country.dialCode
                                                                // Remove dial code from phone number
                                                                phoneDigits = phoneDigits.substring(countryDialCode.length)
                                                                break
                                                            }
                                                        }
                                                    }
                                                }

                                                // If phone starts with 0 and no dial code was found, use Bulgaria's dial code as default
                                                // This handles Bulgarian local format: 0XXXXXXXXX
                                                if (phoneDigits.startsWith("0") && dialCode.isEmpty()) {
                                                    val countries = countriesList
                                                    val bulgariaCountry = countries?.find { it.name.equals("България", ignoreCase = true) }
                                                    if (bulgariaCountry != null) {
                                                        dialCode = bulgariaCountry.dialCode
                                                        // Remove leading 0 from phone number (Bulgarian format: 0XXXXXXXXX -> XXXXXXXXX)
                                                        phoneDigits = phoneDigits.substring(1)
                                                    } else {
                                                        // Fallback: if Bulgaria not found but number starts with 0, assume it's Bulgarian
                                                        // and use +359 as dial code
                                                        dialCode = "+359"
                                                        phoneDigits = phoneDigits.substring(1)
                                                    }
                                                }

                                                sharedConfig.companyPhoneNumber = phoneDigits
                                                sharedConfig.companyDialCode = dialCode
                                            }

                                            // Handle email
                                            val email = companyData.company.contacts.email
                                            if (email.isNotBlank()) {
                                                sharedConfig.email = email
                                            }

                                            // Handle signatory names from managers - create list of signatories
                                            val managers = companyData.company.managers
                                            if (managers != null && managers.isNotEmpty()) {
                                                val signatoriesList = managers.map { manager ->
                                                    val managerName = manager.name.trim()
                                                    if (managerName.isNotBlank()) {
                                                        // Split by space and take first element as firstName, last element as lastName
                                                        val nameParts = managerName.split(" ").filter { it.isNotBlank() }
                                                        if (nameParts.isNotEmpty()) {
                                                            val firstName = nameParts.first()
                                                            val lastName = if (nameParts.size > 1) {
                                                                nameParts.last()
                                                            } else {
                                                                ""
                                                            }
                                                            com.example.mystybag.login.model.SignatoryItem(
                                                                firstName = firstName,
                                                                lastName = lastName,
                                                                phoneNumber = "", // Will be filled by user
                                                                dialCode = "" // Will be filled by user
                                                            )
                                                        } else {
                                                            null
                                                        }
                                                    } else {
                                                        null
                                                    }
                                                }.filterNotNull()

                                                sharedConfig.signatories = signatoriesList
                                            } else {
                                                // If no managers, create empty list
                                                sharedConfig.signatories = emptyList()
                                            }

                                            updateState { it.copy(isLoading = false) }

                                            val updatedConfigAfterCompanyBook = config.copy(sharedConfig = sharedConfig)
                                            replaceLast(updatedConfigAfterCompanyBook)

                                            navigate(NavigatorMerchantRegistrationStep1B(sharedConfig = sharedConfig))
                                        }
                                    } catch (e: Exception) {
                                        launchOnMain {
                                            updateState { it.copy(isLoading = false) }
                                            // Silently continue - user can fill manually if CompanyBook lookup fails
                                            navigate(NavigatorMerchantRegistrationStep1B(sharedConfig = sharedConfig))
                                        }
                                    }
                                }
                            }
                        },
                        baseError = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                            }
                        }
                    )
                }
            }
        }
    }

    private fun clearLoadedRegistrationData() {
        sharedConfig.legalEntityName = ""
        sharedConfig.companyPhoneNumber = ""
        sharedConfig.companyDialCode = ""
        sharedConfig.registeredCompanyStreet = ""
        sharedConfig.registeredCompanyCityId = null
        sharedConfig.registeredCompanyCityName = ""
        sharedConfig.registeredCompanyCountryId = null
        sharedConfig.registeredCompanyCountryName = ""
        sharedConfig.companyImageFileName = null
        sharedConfig.signatories = emptyList()
        sharedConfig.iban = ""
        sharedConfig.email = ""
        sharedConfig.password = ""
    }
}
